<template>    
    <main class="min-h-screen bg-white overflow-hidden flex flex-col">
      <!-- Background elements -->
      <UnderwaterBg class="fixed inset-0 z-0" />
      
      <!-- Header/Navigation -->
      <the-header
        :is-user-authenticated="isUserAuthenticated"
        @open-login="showLoginDialog = true"
      />
  
      <!-- Main Content -->
      <div class="relative z-10 px-6 md:px-10 max-w-7xl mx-auto py-12 md:py-20 flex-grow">
        <!-- Page Title -->
        <Motion
          :initial="{ opacity: 0, y: 30 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.7 }"
          class="text-center mb-12"
        >          <h1 class="text-4xl md:text-5xl font-bold mb-4 text-slate-800">
            Contact <span class="text-yellow-500">Us</span>
          </h1>
          <p class="text-lg text-gray-700 max-w-2xl mx-auto">
            Have questions about DuckNSeek? We're here to help! Send us a message and we'll get back to you as soon as possible.
          </p>
        </Motion>
        
        <!-- Contact Content -->
        <div class="grid md:grid-cols-2 gap-12 items-start">
          <!-- Contact Form -->
          <Motion
            :initial="{ opacity: 0, x: -50 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.7, delay: 0.2 }"
            class="bg-white/80 backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-lg"
          >
            <h2 class="text-2xl font-bold mb-6 text-slate-800">Send Us a Message</h2>
            
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <!-- Name Field -->
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                <input
                  v-model="formData.name"
                  type="text"
                  required
                  class="w-full rounded-lg border border-gray-300 px-4 py-3 bg-white/70 text-gray-900 shadow-sm transition-all focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20"
                  :class="{'border-red-500 focus:border-red-500': errors.name}"
                  placeholder="John Doe"
                  @input="validateName"
                />
                <p v-if="errors.name" class="mt-1 text-sm text-red-500">{{ errors.name }}</p>
              </div>
              
              <!-- Email Field -->
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700  mb-1">Email Address</label>
                <input
                  v-model="formData.email"
                  type="email"
                  required
                  class="w-full rounded-lg border border-gray-300  px-4 py-3 bg-white/70  text-gray-900  shadow-sm transition-all focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20"
                  :class="{'border-red-500 focus:border-red-500': errors.email}"
                  placeholder="john@example.com"
                  @input="validateEmail"
                />
                <p v-if="errors.email" class="mt-1 text-sm text-red-500">{{ errors.email }}</p>
              </div>
              
              <!-- Subject Field -->
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700  mb-1">Subject</label>
                <input
                  v-model="formData.subject"
                  type="text"
                  class="w-full rounded-lg border border-gray-300  px-4 py-3 bg-white/70  text-gray-900  shadow-sm transition-all focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20"
                  :class="{'border-red-500 focus:border-red-500': errors.subject}"
                  placeholder="Inquiry about DuckNSeek"
                  @input="validateSubject"
                />
                <p v-if="errors.subject" class="mt-1 text-sm text-red-500">{{ errors.subject }}</p>
              </div>
              
              <!-- Message Field -->
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700  mb-1">Your Message</label>
                <textarea
                  v-model="formData.message"
                  rows="5"
                  required
                  class="w-full rounded-lg border border-gray-300  px-4 py-3 bg-white/70  text-gray-900  shadow-sm transition-all focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 resize-none"
                  :class="{'border-red-500 focus:border-red-500': errors.message}"
                  placeholder="I'm interested in learning more about DuckNSeek..."
                  @input="validateMessage"
                ></textarea>
                <p v-if="errors.message" class="mt-1 text-sm text-red-500">{{ errors.message }}</p>
              </div>
              
              <!-- Submit Button -->
              <ShimmerButton
                type="submit"
                shimmer-color="rgba(255, 255, 255, 0.8)"
                background="#eab308"
                border-radius="8px"
                class="w-full py-3 font-medium text-white disabled:opacity-70"
                :disabled="isSubmitting || !isFormValid"
              >
                <span v-if="isSubmitting">Sending...</span>
                <span v-else>Send Message</span>
              </ShimmerButton>
            </form>
          </Motion>
          
          <!-- Contact Information -->
          <Motion
            :initial="{ opacity: 0, x: 50 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.7, delay: 0.4 }"
            class="space-y-8"
          >
            <!-- Company Info Card -->
            <div class="bg-white/80  backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-lg">
              <h2 class="text-2xl font-bold mb-6 text-slate-800 ">Get in Touch</h2>
              
              <div class="space-y-4">
                <div class="flex items-start gap-4">
                  <div class="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center mt-1 flex-shrink-0">
                    <v-icon color="yellow">mdi-email</v-icon>
                  </div>
                  <div>
                    <h3 class="font-semibold text-slate-800 ">Email Address</h3>
                    <p class="text-gray-700 ">info@ducknseek.com</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- FAQ Card -->
            <div class="bg-white/80  backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-lg">
              <h2 class="text-2xl font-bold mb-6 text-slate-800 ">Frequently Asked Questions</h2>
              
              <div class="space-y-4">
                <div v-for="(item, index) in faqs" :key="index" class="border-b border-gray-200  pb-4 last:border-0 last:pb-0">
                  <h3 class="font-semibold text-slate-800  mb-2">{{ item.question }}</h3>
                  <p class="text-gray-700 ">{{ item.answer }}</p>
                </div>
              </div>
            </div>
          </Motion>
        </div>
      </div>
      
      <!-- Login Dialog Component -->
      <LoginDialog
        v-model="showLoginDialog"
        :redirect-url="redirectUrl"
        :show-message="loginMessage"
        @notification="showNotificationMessage"
      />
      
      <!-- Notifications -->
      <v-snackbar
        v-model="showNotification"
        :color="notificationColor"
        :timeout="5000"
        location="top"
      >
        {{ notificationMessage }}
        
        <template v-slot:actions>
          <v-btn icon variant="text" @click="showNotification = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
  
      <!-- Footer -->
      <div class="relative z-10 w-full mt-auto">
        <the-footer />
      </div>
    </main>
</template>
  
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { UnderwaterBg } from '@/components/ui/underwaterBg';
import { useAuthStore } from '@/stores/authStore';
import { Motion } from 'motion-v';
import { ShimmerButton } from '@/components/ui/shimmerButton';
import TheHeader from '@/components/layout/TheHeader.vue';
import TheFooter from '@/components/layout/TheFooter.vue';
import LoginDialog from '@/components/auth/LoginDialog.vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { Constant } from '@/config/constants';

// Router and route
const route = useRoute();

// Auth store
const authStore = useAuthStore();

// Computed property to check if user is authenticated
const isUserAuthenticated = computed(() => {
  return authStore.isAuthenticated();
});

// Form data structure matching the ContactRequest schema from the API
const formData = ref({
  name: '',
  email: '',
  subject: '', // Optional field
  message: '',
});

// Form errors
const errors = ref({
  name: '',
  email: '',
  subject: '',
  message: '',
});

// Form and notification states
const isSubmitting = ref(false);
const showLoginDialog = ref(false);
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationColor = ref('success');

// Add redirectUrl and loginMessage variables for LoginDialog
const redirectUrl = ref(route.query.redirect as string || '');
const loginMessage = ref('');

// FAQs data
const faqs = ref([
  {
    question: 'How does DuckNSeek work?',
    answer: 'Participants hide rubber ducks around the cruise ship, each with a QR code. When someone finds a duck, they scan the code to leave a message, and the original owner gets notified.'
  },
  {
    question: 'How many ducks should I order?',
    answer: 'It depends on your group size. Most cruisers find 20-50 ducks to be ideal for a standard 7-day cruise.'
  },
  {
    question: 'How far in advance should I order?',
    answer: 'We recommend ordering at least 3 weeks before your cruise to ensure timely delivery and setup.'
  }
]);

// Form validation
const validateName = () => {
  if (!formData.value.name.trim()) {
    errors.value.name = 'Name is required';
  } else if (formData.value.name.trim().length < 2) {
    errors.value.name = 'Name must be at least 2 characters';
  } else {
    errors.value.name = '';
  }
};

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formData.value.email.trim()) {
    errors.value.email = 'Email is required';
  } else if (!emailRegex.test(formData.value.email.trim())) {
    errors.value.email = 'Please enter a valid email address';
  } else {
    errors.value.email = '';
  }
};

const validateSubject = () => {
  if (formData.value.subject.trim() && formData.value.subject.trim().length < 3) {
    errors.value.subject = 'Subject must be at least 3 characters';
  } else {
    errors.value.subject = '';
  }
};

const validateMessage = () => {
  if (!formData.value.message.trim()) {
    errors.value.message = 'Message is required';
  } else if (formData.value.message.trim().length < 10) {
    errors.value.message = 'Message must be at least 10 characters';
  } else {
    errors.value.message = '';
  }
};

// Check if form is valid
const isFormValid = computed(() => {
  return (
    formData.value.name.trim() !== '' &&
    formData.value.email.trim() !== '' &&
    formData.value.message.trim() !== '' &&
    !errors.value.name &&
    !errors.value.email &&
    !errors.value.subject &&
    !errors.value.message
  );
});

// Submit form - Updated to use the actual API endpoint
const handleSubmit = async () => {
  // Validate all fields first
  validateName();
  validateEmail();
  validateSubject();
  validateMessage();
  
  if (!isFormValid.value) return;
  
  isSubmitting.value = true;
  
  try {
    // Call the contact API endpoint
    await axios.post(`${Constant.API_URL}/contact`, {
      name: formData.value.name.trim(),
      email: formData.value.email.trim(),
      subject: formData.value.subject.trim() || undefined, // Only include if not empty
      message: formData.value.message.trim()
    });
    
    // Show success message
    notificationMessage.value = 'Your message has been sent successfully! We will get back to you soon.';
    notificationColor.value = 'success';
    showNotification.value = true;
    
    // Reset form
    formData.value = {
      name: '',
      email: '',
      subject: '',
      message: ''
    };
  } catch (error: any) {
    console.error('Failed to send message:', error);
    
    // Handle different error responses based on status code
    if (error.response) {
      switch (error.response.status) {
        case 400:
          notificationMessage.value = error.response.data?.message || 'Please check your form data and try again.';
          notificationColor.value = 'error';
          break;
        case 429:
          notificationMessage.value = 'You have sent too many messages. Please try again later.';
          notificationColor.value = 'warning';
          break;
        default:
          notificationMessage.value = 'Failed to send message. Please try again later.';
          notificationColor.value = 'error';
      }
    } else {
      notificationMessage.value = 'Network error. Please check your connection and try again.';
      notificationColor.value = 'error';
    }
    
    showNotification.value = true;
  } finally {
    isSubmitting.value = false;
  }
};

// Handle notifications - simplified to match HomeView implementation
const showNotificationMessage = ({ message, color }: { message: string, color?: string }) => {
  notificationMessage.value = message;
  notificationColor.value = color || 'info';
  showNotification.value = true;
};

onMounted(() => {
  // Initialize authentication state
  authStore.initAuth();
});
</script>
  
<style scoped>
.form-group {
  position: relative;
  transition: all 0.3s ease;
}

.form-group:focus-within label {
  color: #eab308;
}

/* Animation for form elements */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.form-group {
  animation: fadeIn 0.5s forwards;
  animation-delay: calc(0.1s * var(--index, 0));
}

/* Improve accessibility states */
input:focus,
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.3);
}
</style>